import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import ProgressiveList from "./ProgressiveList";

/**
 * Client-side pagination that respects group boundaries.
 *
 * Groups (typically Base Game and each DLC) are packed into pages of at most
 * `pageSize` achievements each. The greedy algorithm is:
 *   • Process groups in the order they are provided.
 *   • Try to fit each group entirely on the current page. If it fits, add it.
 *   • Otherwise, if the group could fit on a full empty page and the current
 *     page already has items, flush the current page and put the group on a
 *     fresh page (so the group stays intact when possible).
 *   • Otherwise (group is larger than a full page), split the group across
 *     as many pages as needed, using 100% of each page.
 *
 * Result: given base=153, dlc1=40, dlc2=30 with pageSize=100, pages are
 *   [ 100 base ] [ 53 base + 40 dlc1 ] [ 30 dlc2 ]
 *
 * DLC group headers render inline at the top of each contiguous segment on a
 * page so the user still sees which group each achievement belongs to.
 *
 * Props:
 *   groups     — Array<{ id, label, items }>
 *   renderItem — (item, groupId) => ReactNode
 *   pageSize   — default 100
 *   activateAt — total-items threshold below which pagination is skipped;
 *                defaults to `pageSize`. Pass 500 to only paginate large games.
 */
export default function PackedGroupedList({
  groups,
  renderItem,
  pageSize = 100,
  activateAt = null,
  // Callback: receives {groupId -> pageNumber} whenever the packing changes.
  // Consumers use this to build a "jump to DLC" nav.
  onGroupPageMap,
  // Controlled jump — when this changes to a valid groupId, PackedGroupedList
  // will navigate to the page containing that group's first appearance and
  // call `onJumpConsumed` to signal completion.
  jumpToGroupId,
  onJumpConsumed,
}) {
  const [page, setPage] = useState(1);
  const topRef = useRef(null);
  // Groups the user has collapsed via their sticky header. Collapsing hides
  // that group's achievements on every page while keeping the header pinned.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // Toggle collapse for a group, anchoring the scroll so the header stays put
  // in the viewport (the page must not jump when content above/below folds).
  const toggleGroupCollapse = (groupId, headerEl) => {
    const oldTop = headerEl ? headerEl.getBoundingClientRect().top : null;
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
    requestAnimationFrame(() => {
      if (!headerEl || oldTop == null) return;
      const newTop = headerEl.getBoundingClientRect().top;
      const delta = newTop - oldTop;
      if (delta !== 0) window.scrollBy(0, delta);
    });
  };

  const totalItems = groups.reduce((s, g) => s + (g.items?.length || 0), 0);
  const threshold = activateAt ?? pageSize;
  const paginate = totalItems > threshold;

  // Pack groups into pages of at most `pageSize` items. Memoised so the
  // arithmetic isn't repeated on every render.
  const pages = useMemo(() => {
    if (!paginate) {
      // Single page containing every group in order.
      return [{
        segments: groups
          .filter(g => (g.items?.length || 0) > 0)
          .map(g => ({ groupId: g.id, groupLabel: g.label, iconUrl: g.iconUrl, items: g.items })),
        count: totalItems,
      }];
    }
    return buildPackedPages(groups, pageSize);
  }, [groups, pageSize, paginate, totalItems]);

  const totalPages = Math.max(1, pages.length);

  // Publish groupId -> page number map upward whenever packing changes so
  // callers can build a DLC nav that jumps to specific pages. Guard with a
  // ref-based deep compare — the parent's `groups` array is often built
  // inline, so `pages` gets a new reference on every render even when the
  // packing is identical. Without this guard we'd bounce parent state on
  // every render and hang the tab on large games.
  const lastEmittedMapRef = useRef("");
  useEffect(() => {
    if (!onGroupPageMap) return;
    const map = {};
    pages.forEach((p, i) => {
      p.segments.forEach(seg => {
        if (map[seg.groupId] === undefined) map[seg.groupId] = i + 1;
      });
    });
    const serialized = JSON.stringify(map);
    if (serialized === lastEmittedMapRef.current) return;
    lastEmittedMapRef.current = serialized;
    onGroupPageMap(map);
  }, [pages, onGroupPageMap]);

  // Handle controlled jumps requested by the parent.
  useEffect(() => {
    if (!jumpToGroupId) return;
    const map = {};
    pages.forEach((p, i) => {
      p.segments.forEach(seg => {
        if (map[seg.groupId] === undefined) map[seg.groupId] = i + 1;
      });
    });
    const target = map[jumpToGroupId];
    if (target && target !== page) {
      setPage(target);
      requestAnimationFrame(() => {
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    onJumpConsumed?.();
  }, [jumpToGroupId, pages, page, onJumpConsumed]);

  // Clamp page when the underlying list shrinks below the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const goToPage = (n) => {
    const clamped = Math.min(Math.max(1, n), totalPages);
    if (clamped === page) return;
    setPage(clamped);
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const currentPage = pages[Math.min(page, pages.length) - 1] || { segments: [], count: 0 };

  // Cumulative achievement index range shown on this page, for the label.
  const startIdx = pages.slice(0, page - 1).reduce((s, p) => s + p.count, 0) + 1;
  const endIdx = startIdx + currentPage.count - 1;

  return (
    <>
      <div ref={topRef} className="scroll-mt-4" />
      {paginate && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          startIdx={startIdx}
          endIdx={endIdx}
          total={totalItems}
          onPageChange={goToPage}
        />
      )}
      {currentPage.segments.map((seg, i) => {
        const collapsed = collapsedGroups.has(seg.groupId);
        const hasHeader = paginate || i > 0;
        return (
          <div key={`${seg.groupId}-${i}`} className="space-y-3">
            {hasHeader && (
              <GroupHeader
                label={seg.groupLabel}
                iconUrl={seg.iconUrl}
                testId={`packed-group-${seg.groupId}`}
                collapsed={collapsed}
                itemCount={seg.items.length}
                onToggle={(el) => toggleGroupCollapse(seg.groupId, el)}
              />
            )}
            {!collapsed && (
              <ProgressiveList items={seg.items} renderItem={(it) => renderItem(it, seg.groupId)} />
            )}
          </div>
        );
      })}
      {paginate && (
        <PaginationBar
          page={page}
          totalPages={totalPages}
          startIdx={startIdx}
          endIdx={endIdx}
          total={totalItems}
          onPageChange={goToPage}
        />
      )}
    </>
  );
}

function GroupHeader({ label, iconUrl, testId, collapsed, itemCount, onToggle }) {
  // Sticky so the current DLC/section label stays pinned to the top of the
  // achievement list as the user scrolls through that group's achievements.
  // A solid dark background prevents the achievements below from bleeding
  // through when the header is stuck. Clicking it collapses/expands the group.
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className="sticky top-24 md:top-0 z-20 -mx-4 px-4 py-2 mt-3 bg-[#0A0A0A]/95 backdrop-blur border-y border-white/10 flex items-center gap-3 cursor-pointer select-none hover:bg-[#141414]/95 transition-colors"
      data-testid={testId}
      onClick={() => onToggle?.(ref.current)}
      role="button"
      aria-expanded={!collapsed}
    >
      <ChevronDown
        className={`w-4 h-4 shrink-0 text-white/50 transition-transform ${collapsed ? "-rotate-90" : ""}`}
      />
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className="w-8 h-8 rounded object-cover border border-white/10 shrink-0"
        />
      ) : (
        <span className="inline-block w-1.5 h-6 rounded bg-cyan-400/70 shrink-0" aria-hidden="true" />
      )}
      <span className="text-sm font-semibold text-white/90 tracking-wide truncate">
        {label}
      </span>
      {collapsed && itemCount != null && (
        <span className="ml-auto text-xs text-white/40 tabular-nums shrink-0">{itemCount} hidden</span>
      )}
    </div>
  );
}

function PaginationBar({ page, totalPages, startIdx, endIdx, total, onPageChange }) {
  const pages = buildPageList(page, totalPages);
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 py-2 my-2 border-y border-white/5 text-xs text-white/60"
      data-testid="pagination-bar"
    >
      <span className="tabular-nums">
        {startIdx}–{endIdx} of {total} achievements
      </span>
      <div className="flex items-center gap-1">
        <PageBtn onClick={() => onPageChange(page - 1)} disabled={page === 1} title="Previous page">
          <ChevronLeft className="w-3.5 h-3.5" />
        </PageBtn>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-1 text-white/30 select-none">…</span>
          ) : (
            <PageBtn key={p} onClick={() => onPageChange(p)} active={p === page} title={`Go to page ${p}`}>
              {p}
            </PageBtn>
          )
        )}
        <PageBtn onClick={() => onPageChange(page + 1)} disabled={page === totalPages} title="Next page">
          <ChevronRight className="w-3.5 h-3.5" />
        </PageBtn>
      </div>
    </div>
  );
}

function PageBtn({ onClick, disabled, active, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`min-w-[1.75rem] h-7 px-2 rounded text-xs tabular-nums flex items-center justify-center border transition-colors ${
        active
          ? "bg-cyan-500/25 border-cyan-400/60 text-cyan-200 font-semibold"
          : "border-white/10 hover:border-white/30 hover:bg-white/5 text-white/70 disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:bg-transparent"
      }`}
    >
      {children}
    </button>
  );
}

// ============ helpers ============

function buildPackedPages(groups, pageSize) {
  const pages = [];
  let current = { segments: [], count: 0 };

  const push = () => {
    if (current.count > 0) {
      pages.push(current);
      current = { segments: [], count: 0 };
    }
  };

  for (const group of groups) {
    const items = group.items || [];
    if (items.length === 0) continue;

    let offset = 0;
    while (offset < items.length) {
      const remaining = items.length - offset;
      const space = pageSize - current.count;

      if (remaining <= space) {
        // Whole remainder fits on the current page.
        current.segments.push({
          groupId: group.id,
          groupLabel: group.label,
          iconUrl: group.iconUrl,
          items: items.slice(offset, offset + remaining),
        });
        current.count += remaining;
        offset += remaining;
        continue;
      }

      // If we could fit the whole remainder on an empty page AND the
      // current page already has items, flush and start fresh so the
      // group stays together.
      if (remaining <= pageSize && current.count > 0) {
        push();
        continue;
      }

      // Group's remainder is bigger than a full page — split. Fill current
      // page with as much as fits and flush.
      current.segments.push({
        groupId: group.id,
        groupLabel: group.label,
        iconUrl: group.iconUrl,
        items: items.slice(offset, offset + space),
      });
      current.count += space;
      offset += space;
      push();
    }
  }
  push();
  return pages.length ? pages : [{ segments: [], count: 0 }];
}

function buildPageList(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const set = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const withGaps = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) withGaps.push("…");
    withGaps.push(sorted[i]);
  }
  return withGaps;
}
